import { ArrowRight } from "lucide-react";
import { MaskedLines, Overline, Reveal, Section } from "./ui";
import { scrollToId } from "@/lib/scroll";

const escada = [
  "Conversas abertas",
  "Clientes interessadas",
  "Clientes que deveriam retornar",
  "Clientes antigas",
  "Varejo",
  "Cliente Traz Cliente",
  "Influência x Receita",
  "Oferta direcionada",
  "Condição comercial",
  "Promoção",
];

const caixaAtivos = ["clientes", "produtos", "tratamentos", "horários", "orçamentos", "estoque", "serviços futuros"];

const vendaFutura = [
  "tratamento pré-pago",
  "produto com retirada posterior",
  "produto com entrega",
  "voucher",
  "serviço futuro",
  "condição para horários fracos",
];

const influenciaLocal = [
  "a academia do bairro",
  "a loja próxima",
  "uma fotógrafa",
  "uma maquiadora",
  "uma cliente muito conectada",
  "uma criadora local",
];

const diasRecepcao = ["Abertura", "Atendimento", "Mensagens", "Confirmações", "Relacionamento", "Fechamento", "Autonomia"];

function ContextoBand() {
  return (
    <section data-testid="mom-contexto" className="relative min-h-[70vh] overflow-hidden bg-black text-white">
      <img loading="lazy" src="/mom/contexto-c.jpg" alt="Salão em funcionamento, equipe atendendo clientes" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />
      <div className="relative mx-auto flex min-h-[70vh] w-full max-w-6xl flex-col justify-end px-6 pb-16 pt-24 sm:px-10">
        <Reveal>
          <p className="font-dmsans text-[10px] font-semibold uppercase tracking-[0.28em] text-white/60">
            Beleza em movimento
          </p>
        </Reveal>
        <MaskedLines
          inView
          as="h2"
          className="mt-5 font-serif text-5xl font-medium leading-[1.04] tracking-tight sm:text-6xl"
          lines={["O salão", <em key="s" className="italic">não para.</em>]}
        />
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-lg font-dmsans text-sm leading-relaxed text-white/75 sm:text-base">
            Enquanto clientes chegam, mensagens aparecem e horários mudam, o MOM
            ajuda você a escolher o próximo movimento.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function DeepDive() {
  return (
    <>
      {/* Tempo livre — black, editorial */}
      <Section tone="black">
        <div className="max-w-3xl">
          <Overline>Tempo Livre</Overline>
          <MaskedLines
            inView
            as="h2"
            className="mt-5 font-serif text-3xl font-medium leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl"
            lines={["Uma hora vazia não precisa", "virar uma hora perdida"]}
          />
          <Reveal delay={0.15} className="mt-8 font-dmsans text-sm leading-relaxed text-white/55 sm:text-base">
            <p>
              Uma janela de 30 minutos não precisa virar 30 minutos de tarefas
              aleatórias. Ela pode servir para gerar receita, recuperar uma
              cliente, organizar algo importante, estudar ou até descansar
              conscientemente.
            </p>
          </Reveal>
          <Reveal delay={0.25} className="mt-10 border-l-2 border-mom-carmim pl-6">
            <p className="font-serif text-xl font-medium italic leading-snug text-white sm:text-2xl">
              Produtividade não é encher todo minuto. É escolher melhor o que
              merece aquele minuto.
            </p>
            <p className="mt-5 font-dmsans text-sm text-white/55 sm:text-base">
              A pergunta do MOM não é{" "}
              <span className="line-through decoration-white/40">“Como ocupar esse tempo?”</span>{" "}
              É <span className="font-semibold text-mom-carmim">“O que essa hora merece?”</span>
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Agenda viva — cream */}
      <Section tone="white">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <div>
            <Overline>Agenda Viva</Overline>
            <MaskedLines
              inView
              as="h2"
              className="mt-5 font-serif text-3xl font-medium leading-[1.1] tracking-tight text-hc-ink sm:text-4xl lg:text-5xl"
              lines={["Agenda fraca não significa", "falta de cliente"]}
            />
            <Reveal delay={0.15} className="mt-8 space-y-5 font-dmsans text-sm leading-relaxed text-hc-mutedInk sm:text-base">
              <p>
                Antes de baixar preço, o MOM ajuda você a procurar
                oportunidades que já existem.
              </p>
              <p>
                Uma cliente pode ter gostado e simplesmente não ter sido chamada
                novamente. Um orçamento pode ter esfriado. Uma indicação pode nunca
                ter sido pedida. Uma parceria pode continuar apenas na ideia.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-8 font-serif text-xl font-medium italic text-hc-ink sm:text-2xl">
                Primeiro recupere oportunidades.{" "}
                <span className="text-mom-carmim">Depois mexa na margem.</span>
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="rounded-3xl bg-white p-8 shadow-[0_20px_60px_rgba(0,0,0,0.06)] sm:p-10">
            <p className="font-dmsans text-[11px] font-semibold uppercase tracking-[0.24em] text-mom-vinho">
              Escada Agenda Viva
            </p>
            <p className="mt-2 font-dmsans text-xs text-hc-mutedInk">
              Antes de uma promoção agressiva, o sistema pode avaliar:
            </p>
            <ol className="mt-6 divide-y divide-black/10 border-y border-black/10">
              {escada.map((e, i) => (
                <li key={e} data-testid={`escada-step-${i}`} className="flex items-center gap-4 py-3">
                  <span className={`font-serif text-base font-semibold ${i === escada.length - 1 ? "text-mom-carmim" : "text-black/25"}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className={`font-dmsans text-sm ${i === escada.length - 1 ? "font-semibold text-hc-ink" : "text-hc-ink/80"}`}>
                    {e}
                    {i === escada.length - 1 && (
                      <span className="ml-2 text-xs font-normal text-hc-mutedInk">— quando realmente fizer sentido</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </Section>

      {/* Contexto real do salão — imagem C full-bleed */}
      <ContextoBand />

      {/* Atender não é o fim — black, editorial */}
      <Section tone="black">
        <div className="max-w-3xl">
          <Overline>Pós-atendimento</Overline>
          <MaskedLines
            inView
            as="h2"
            className="mt-5 font-serif text-3xl font-medium leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl"
            lines={["Atender não deveria", "ser o fim"]}
          />
          <Reveal delay={0.15} className="mt-8 space-y-5 font-dmsans text-sm leading-relaxed text-white/55 sm:text-base">
            <p className="font-serif text-lg font-medium text-white sm:text-xl">
              A cliente veio. Foi atendida. Pagou. Saiu.
            </p>
            <p>
              Se ninguém acompanha, lembra do retorno ou cria uma próxima
              oportunidade, aquele atendimento pode morrer ali. O processo real
              continua depois da cadeira.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-10 font-serif text-2xl font-medium leading-relaxed text-white sm:text-3xl">
              Atrair. Atender. Registrar.{" "}
              <span className="text-mom-carmim">Acompanhar. Retornar.</span>
            </p>
            <p className="mt-6 max-w-lg font-dmsans text-sm leading-relaxed text-white/55 sm:text-base">
              É exatamente essa continuidade que sustenta recorrência e
              relacionamento. O MOM ajuda a transformar uma cliente atendida em
              uma próxima ação possível.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Caixa hoje — cream */}
      <Section tone="white">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2">
          <div>
            <Overline>Caixa Hoje</Overline>
            <MaskedLines
              inView
              as="h2"
              className="mt-5 font-serif text-3xl font-medium leading-[1.1] tracking-tight text-hc-ink sm:text-4xl lg:text-5xl"
              lines={["Caixa hoje não significa", "desconto hoje"]}
            />
            <Reveal delay={0.15} className="mt-8 font-dmsans text-sm leading-relaxed text-hc-mutedInk sm:text-base">
              <p>Pode existir dinheiro parado em:</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {caixaAtivos.map((c) => (
                  <span key={c} className="rounded-full border border-black/10 bg-white px-4 py-1.5 font-dmsans text-xs font-medium text-hc-ink">
                    {c}
                  </span>
                ))}
              </div>
              <p className="mt-6">
                O MOM procura primeiro os ativos que já existem. Depois
                transforma um deles em missão.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="rounded-3xl bg-black p-8 text-white sm:p-10">
            <p className="font-dmsans text-[11px] font-semibold uppercase tracking-[0.24em] text-mom-carmim">
              Venda futura, caixa hoje
            </p>
            <p className="mt-3 font-serif text-xl font-medium sm:text-2xl">
              Receba hoje por algo que a cliente pode usar depois.
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-x-6 sm:grid-cols-2">
              {vendaFutura.map((v) => (
                <li key={v} className="border-b border-white/10 py-3 font-dmsans text-sm text-white/75">
                  {v}
                </li>
              ))}
            </ul>
            <p className="mt-6 font-dmsans text-xs text-white/45">
              Sempre considerando margem, capacidade e validade.
            </p>
            <p className="mt-6 border-t border-dashed border-white/15 pt-6 font-serif text-lg font-medium italic">
              A proposta não é vender barato. É encontrar uma forma inteligente de
              trazer caixa para o presente.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Comercial — imagem B protagonista */}
      <Section tone="paper">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="font-dmsans text-[10px] font-semibold uppercase tracking-[0.28em] text-black/50">
                Mais clientes. Mais resultados.
              </p>
            </Reveal>
            <MaskedLines
              inView
              as="h2"
              className="mt-6 font-serif text-4xl font-medium leading-[1.06] tracking-tight text-hc-ink sm:text-5xl"
              lines={["Cliente Traz Cliente.", <em key="c" className="italic text-mom-carmim">Caixa Hoje.</em>]}
            />
            <Reveal delay={0.15}>
              <p className="mt-7 max-w-md font-dmsans text-sm leading-relaxed text-black/60 sm:text-base">
                O MOM transforma clientes, produtos, horários e oportunidades
                esquecidas em uma missão comercial prática para executar agora.
              </p>
              <p className="mt-5 max-w-md font-serif text-lg font-medium italic leading-snug text-hc-ink">
                “Indica alguém e eu te dou um desconto”{" "}
                <span className="font-dmsans text-sm not-italic text-hc-mutedInk">
                  costuma nascer sem regra, sem cálculo e sem saber se a conta
                  fecha. Aqui, indicação vira ação organizada.
                </span>
              </p>
              <button
                data-testid="comercial-cta"
                onClick={() => scrollToId("#oferta")}
                className="group mt-9 inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-mom-vinho to-mom-carmim px-7 py-3.5 font-dmsans text-sm font-semibold uppercase tracking-[0.08em] text-white transition-[filter] hover:brightness-110"
              >
                Começar agora
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="overflow-hidden rounded-3xl">
              <img loading="lazy" src="/mom/comercial-b.jpg" alt="Profissional agindo no celular dentro do salão" className="aspect-[3/4] w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
            <p className="absolute -left-4 bottom-10 hidden font-script text-3xl font-semibold text-mom-carmim [writing-mode:vertical-rl] lg:block">
              um amanhã mais seu
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Influência x Receita — cream, editorial */}
      <Section tone="white">
        <div className="max-w-3xl">
          <Overline>Influência x Receita</Overline>
          <MaskedLines
            inView
            as="h2"
            className="mt-5 font-serif text-3xl font-medium leading-[1.1] tracking-tight text-hc-ink sm:text-4xl lg:text-5xl"
            lines={["Influência pode estar", <em key="i" className="italic text-mom-carmim">na mesma rua.</em>]}
          />
          <Reveal delay={0.15} className="mt-8 font-dmsans text-sm leading-relaxed text-hc-mutedInk sm:text-base">
            <p>
              Academias, lojas, fotógrafos, maquiadores, criadores locais e
              parceiros do bairro podem virar ações concretas de aproximação.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-7 flex flex-wrap gap-2">
            {influenciaLocal.map((i) => (
              <span key={i} className="rounded-full border border-black/10 bg-white px-4 py-1.5 font-dmsans text-xs font-medium text-hc-ink">
                {i}
              </span>
            ))}
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-8 inline-block rounded-full border border-mom-carmim/30 bg-mom-carmim/5 px-5 py-2.5 font-dmsans text-xs font-semibold text-mom-vinho">
              Exemplo de missão: 3 contatos feitos e 1 proposta enviada.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Basic Recepção + Bônus — black */}
      <Section tone="black">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Reveal className="rounded-3xl border border-white/10 bg-hc-deep p-10 sm:p-12">
            <Overline>Basic Recepção</Overline>
            <h3 className="mt-4 font-serif text-2xl font-medium leading-snug tracking-tight text-white sm:text-3xl">
              Quando ninguém está na recepção, o essencial não pode parar.
            </h3>
            <div className="mt-6 space-y-4 font-dmsans text-sm leading-relaxed text-white/55 sm:text-base">
              <p>
                Cliente continua chegando. WhatsApp continua recebendo mensagem.
                Agenda continua mudando. Confirmações continuam precisando
                acontecer.
              </p>
              <p>
                O Basic Recepção mostra o mínimo operacional que precisa continuar
                funcionando naquele momento.{" "}
                <span className="font-semibold text-white">
                  Não é fazer tudo. É não esquecer o que não pode parar.
                </span>
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-mom-vinho to-mom-vinhoDark p-10 text-white sm:p-12">
            <span className="absolute right-8 top-8 rounded-full bg-white px-3 py-1 font-dmsans text-[10px] font-semibold uppercase tracking-[0.18em] text-mom-vinho">
              Bônus incluso
            </span>
            <p className="font-dmsans text-[11px] font-semibold uppercase tracking-[0.3em] text-white/70">Recepção 7 Dias</p>
            <h3 className="mt-4 max-w-sm font-serif text-2xl font-medium tracking-tight sm:text-3xl">
              Pare de treinar recepcionista no improviso.
            </h3>
            <p className="mt-6 max-w-md font-dmsans text-sm leading-relaxed text-white/70 sm:text-base">
              Fluxo progressivo para orientar uma nova profissional nas rotinas
              essenciais da recepção. Cada dia trabalha uma parte da operação.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {diasRecepcao.map((d, i) => (
                <span key={d} data-testid={`recepcao-dia-${i}`} className="flex items-center gap-2 rounded-full border border-white/25 bg-black/15 px-4 py-1.5 font-dmsans text-xs font-medium text-white/90">
                  <span className="font-serif font-semibold text-white">{i + 1}º</span> {d}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
